// What a guest keeps in the browser while tasting from a QR code before
// signing in. Ported from the legacy guestStorage.ts, same keys and shapes
// so a browser that stored notes under the legacy app still hands them over.
//
//   winote_guest_notes  GuestNote[]  {id, createdAt, savedAt, eventId,
//                                     eventWineId, wineId, joinCode, draft}
//   winote_guest_event  {savedAt, event: GuestEvent}  (a bare GuestEvent
//                                                     from before the
//                                                     envelope still reads)
//
// Three notes at most. Notes older than seven days (by savedAt; a note with
// none never expires) are dropped on read, and an envelope older than seven
// days reads as nothing.

const NOTES_KEY = "winote_guest_notes"
const EVENT_KEY = "winote_guest_event"
export const GUEST_NOTE_LIMIT = 3
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

function fresh(stamp) {
  return !stamp || Date.now() - new Date(stamp).getTime() < SEVEN_DAYS_MS
}

export function getGuestNotes() {
  try {
    const raw = localStorage.getItem(NOTES_KEY)
    if (!raw) return []
    const items = JSON.parse(raw)
    if (!Array.isArray(items)) return []
    return items.filter((item) => item && typeof item === "object" && fresh(item.savedAt))
  } catch {
    return []
  }
}

// False when the browser refused the write (quota, private mode).
export function saveGuestNote(note) {
  try {
    const notes = getGuestNotes()
    notes.push({ ...note, savedAt: new Date().toISOString() })
    localStorage.setItem(NOTES_KEY, JSON.stringify(notes))
    return true
  } catch {
    return false
  }
}

export function getGuestNoteCount() {
  return getGuestNotes().length
}

export function hasReachedGuestLimit() {
  return getGuestNoteCount() >= GUEST_NOTE_LIMIT
}

export function hasGuestNoteForEventWine(eventWineId) {
  return getGuestNotes().some((note) => note.eventWineId === eventWineId)
}

export function getGuestEvent() {
  try {
    const raw = localStorage.getItem(EVENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return null
    if (!("savedAt" in parsed)) return parsed
    return fresh(parsed.savedAt) ? parsed.event : null
  } catch {
    return null
  }
}

export function saveGuestEvent(event) {
  try {
    localStorage.setItem(EVENT_KEY, JSON.stringify({ savedAt: new Date().toISOString(), event }))
  } catch {
    // Private browsing or quota: the guest flow degrades to this visit.
  }
}

export function clearGuestData() {
  try {
    localStorage.removeItem(NOTES_KEY)
    localStorage.removeItem(EVENT_KEY)
  } catch {
    // Nothing to clear, or nowhere to clear it from.
  }
}

// Drop what has expired, so the keys never hold stale data indefinitely.
export function pruneGuestData() {
  try {
    const raw = localStorage.getItem(NOTES_KEY)
    if (raw) {
      const notes = getGuestNotes()
      if (notes.length === 0) localStorage.removeItem(NOTES_KEY)
      else localStorage.setItem(NOTES_KEY, JSON.stringify(notes))
    }
    if (localStorage.getItem(EVENT_KEY) && getGuestEvent() === null) localStorage.removeItem(EVENT_KEY)
  } catch {
    // Nothing to prune.
  }
}
